<template>
    <b-modal
            :visible="show"
            @hidden="hideEvents"
            title="Assign Actions"
            :id="modalId"
    >
        <vue-element-loading :active="loading" :is-full-screen="false"/>

        <b-form>
            <b-form-group
                    label="Assign to user or team"
            >
                <multiselect
                        v-if="!loading"
                        :options="form.reviewer.options"
                        label="label"
                        v-model="form.reviewer.value"
                        group-label="team"
                        group-values="items"
                        :group-select="false"
                        selectLabel=""
                        placeholder=""
                        tagPlaceholder=" "
                        deselectLabel=""
                >
                    <template #caret="{ toggle }">
                        <caret-icon @mousedown.prevent.stop="toggle"></caret-icon>
                    </template>
                </multiselect>
            </b-form-group>
            <b-form-group
                    label="Update internal due date (optional)"
            >
                <date-picker
                        id="internalDueDate"
                        v-model="form.internalDueDate"
                ></date-picker>
                <p class="text-xs text-gray-600 italic">
                  Note that this will overwrite any existing Internal Due Dates for the actions selected.
                </p>
                <b-form-group class="mt-4 mb-0">
                    <b-form-radio-group v-model="form.internalDueDateWeek.value"
                                        buttons
                                        button-variant="outline-primary"
                                        :options="form.internalDueDateWeek.options">
                    </b-form-radio-group>
                </b-form-group>
            </b-form-group>
        </b-form>

        <confirmation-warning
                v-if="showUnlockButton"
                :numberOfItems="this.nodeIds.length"
                @unlocked="unlocked = true">
        </confirmation-warning>

        <template slot="modal-footer">
            <b-button
                    variant="outline-secondary"
                    @click="hideModal">
                Cancel
                <font-awesome-icon
                        :icon="['far', 'times']">
                </font-awesome-icon>
            </b-button>
            <b-button
                    class="btn-arrow"
                    variant="primary"
                    @click="assignActions"
                    :disabled="assignDisabled"
            >
                Assign items
                <font-awesome-icon
                        :icon="['far', 'arrow-right']">
                </font-awesome-icon>
            </b-button>
        </template>
    </b-modal>
</template>

<script>
import CaretIcon from '@/components/Form/CaretIcon';
import ModalMixin from '@/components/Modal/ModalMixin';
import DatePicker from '@/components/Form/DatePicker';
import ConfirmationWarning from '@/components/Notifications/ConfirmationWarning';
import dayjs from 'dayjs';
import ActionAssign from '../../../services/Action/ActionAssign';

export default {
  name: 'Action-Assign-Modal',
  components: {
    CaretIcon,
    DatePicker,
    ConfirmationWarning,
  },
  mixins: [ModalMixin],
  data() {
    return {
      modalId: 'actionAssignModal',
      loading: false,
      unlocked: false,
      unlockIcon: false,
      maxNumberOfSelectedActions: 300,
      form: {
        reviewer: {
          value: [],
          options: [],
        },
        internalDueDate: null,
        internalDueDateWeek: {
          value: null,
          options: [
            {
              text: '-1w',
              value: 1,
            },
            {
              text: '-2w',
              value: 2,
            },
            {
              text: '-4w',
              value: 4,
            },
            {
              text: '-8w',
              value: 8,
            },
          ],
        },
      },
    };
  },
  computed: {
    assignDisabled() {
      return !this.validate() || this.loading;
    },
    requiresApproval() {
      return this.nodeIds.length > this.maxNumberOfSelectedActions;
    },
    showUnlockButton() {
      return this.requiresApproval && this.form.reviewer.value.length !== 0;
    },
  },
  watch: {
    'form.internalDueDate': {
      handler(newDate) {
        if (newDate) {
          this.form.internalDueDateWeek.value = null;
        }
      },
    },
    'form.internalDueDateWeek.value': {
      handler(newWeek) {
        if (newWeek) {
          this.form.internalDueDate = null;
        }
      },
    },
  },
  created() {
    this.getAssignOptions();
  },
  methods: {
    async getAssignOptions() {
      this.loading = true;
      ActionAssign.getFormattedOptions().then((options) => {
        this.form.reviewer.options = options;
        this.loading = false;
      });
    },

    async assignActions() {
      if (!this.validate()) {
        return;
      }

      this.loading = true;

      const { teamId, userId } = this.form.reviewer.value;
      const clientActionIds = this.nodeIds;

      const params = {
        clientActionIds,
        teamId,
        userId,
      };

      if (this.form.internalDueDate) {
        params.internalDueDate = dayjs(this.form.internalDueDate).format('YYYY-MM-DD');
      }

      if (this.form.internalDueDateWeek.value) {
        params.internalDueDateWeek = this.form.internalDueDateWeek.value;
      }

      try {
        const response = await ActionAssign.assign(params);
        const { result, data } = response.data;
        const { success, failed } = data;

        const title = 'Actions assigned';
        let text = `${success} actions have been successfully assigned.`;
        let type = 'success';

        if (failed > 0 || !result) {
          type = 'warn';
          text += ` ${failed} actions failed to be assigned. Please check your email for more information.`;
        }

        this.$notify({
          duration: 7000,
          title,
          text,
          type,
        });
        this.$store.dispatch('refreshActions');
      } catch (e) {
        this.$notify({
          title: 'Error',
          text: 'Something went wrong. Please try again later.',
          type: 'error',
        });
      } finally {
        this.hideModal();
        this.loading = false;
      }
    },

    validate() {
      if (this.form.reviewer.value.length === 0
          || this.nodeIds.length === 0
          || (this.requiresApproval && !this.unlocked)
      ) {
        return false;
      }

      if (this.form.internalDueDate) {
        return dayjs(this.form.internalDueDate).isValid();
      }

      return true;
    },

    hideModalEvents() {
      this.form.reviewer.value = [];
      this.form.internalDueDate = '';
    },
  },
};
</script>

<style lang="scss">
    .modal {
        .btn.btn-arrow {
            min-width: 140px;
        }

        .btn-outline-secondary + .btn-arrow {
            margin: auto;
            margin-right: 0;
        }
    }
</style>
